import React from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCog,
  faDiceD20,
  faBookOpen,
  faTimes,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { Link, Divider, Button } from '@heroui/react';
import { trackEvent } from 'src/utils/analytics';

import logo from '../assets/logo.svg';

interface MenuItem {
  name: string;
  path?: string;
  icon: IconDefinition;
  onClick?: () => void;
  showOnMobile?: boolean;
}

interface SideMenuProps {
  isOpen?: boolean;
  onClose?: () => void;
  onShowIntercom?: () => void;
  isMobile?: boolean;
}

const SUPPORT_URL = 'https://support.thedmsark.com/en/';

const menuItems: MenuItem[] = [
  { name: 'Campaigns', path: '/campaigns', icon: faBookOpen },
  { name: 'Sessions', path: '/sessions', icon: faDiceD20 },
  // { name: 'ScribeAI', path: '/scribe-ai', icon: faBook },
  { name: 'Settings', path: '/settings', icon: faCog },
  {
    name: 'Help',
    icon: faQuestionCircle,
    onClick: () => window.open(SUPPORT_URL, '_blank')
  },
  {
    name: 'Add Bot',
    icon: faDiscord,
    onClick: () => {
      trackEvent('Add Bot Clicked', {
        location: 'side_menu'
      });
      window.open('https://discord.com/oauth2/authorize?client_id=1328480794112426034', '_blank');
    }
  }
];

const SideMenu: React.FC<SideMenuProps> = ({
  isOpen = false,
  onClose,
  onShowIntercom,
  isMobile = false
}) => {
  // Create dynamic menu items including Support on mobile
  const allMenuItems = React.useMemo(() => {
    const items = [...menuItems];
    if (isMobile && onShowIntercom) {
      items.push({
        name: 'Support',
        icon: faQuestionCircle,
        onClick: onShowIntercom,
        showOnMobile: true
      });
    }
    return items;
  }, [isMobile, onShowIntercom]);

  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    }
    if (onClose) {
      onClose();
    }
  };
  return (
    <>
      {/* Desktop sidebar - always visible on lg+ screens */}
      <aside className="hidden lg:flex flex-col h-full w-40 fixed bg-slate-600 dark:bg-content1 border-r border-divider top-0 left-0 z-30">
        <div className="mx-4 my-1">
          <Link href="https://thedmsark.com" className="flex items-center">
            <img src={logo} alt="Logo" className="h-10 w-auto" />
          </Link>
        </div>

        <Divider className="" />

        <nav className="flex-1 mx-4 mt-2">
          <ul className="space-y-2">
            {allMenuItems.map((item, index) => (
              <li key={item.path ?? `action-${index}`}>
                {item.path ? (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center p-2 rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary text-white dark:text-primary-foreground'
                          : 'hover:bg-default-100 text-white hover:text-gray-900 dark:hover:text-gray-200'
                      }`
                    }>
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-2" />
                    {item.name}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="flex items-center p-2 rounded-md transition-colors hover:bg-default-100 text-white w-full text-left hover:text-gray-900 dark:hover:text-gray-200">
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-2" />
                    {item.name}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Mobile sidebar - slide in from left */}
      <aside
        className={`
        lg:hidden fixed top-0 left-0 h-full w-64 bg-slate-600 dark:bg-content1 border-r border-divider z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between mx-4 my-1">
          <Link href="https://thedmsark.com" className="flex items-center">
            <img src={logo} alt="Logo" className="h-10 w-auto" />
          </Link>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={onClose}
            className="text-default-500 hover:text-default-700">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>

        <Divider className="" />

        <nav className="flex-1 mx-4 mt-2">
          <ul className="space-y-2">
            {allMenuItems.map((item, index) => (
              <li key={item.path ?? `action-${index}`}>
                {item.path ? (
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center p-3 rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-default-100 text-c'
                      }`
                    }>
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="flex items-center p-3 rounded-md transition-colors hover:bg-default-100 text-c w-full text-left">
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default SideMenu;
